import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Mail } from 'lucide-react'
import ContactForm from '../ContactForm/ContactForm'
import { sendEmail } from '../../services/emailService'
import { ContactForm as ContactFormType } from '../../types'

interface ContactModalProps {
  isOpen: boolean
  onClose: () => void
  initialSubject?: string
  initialMessage?: string
  source?: string
}

const ContactModal: React.FC<ContactModalProps> = ({
  isOpen,
  onClose,
  initialMessage = '',
  source = 'general'
}) => {
  const { t } = useTranslation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // 处理ESC键关闭
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      // 防止背景滚动
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  // 清理定时器
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  // 处理表单提交
  const handleFormSubmit = async (formData: ContactFormType): Promise<boolean> => {
    setIsSubmitting(true)
    
    try {
      // 添加初始主题和消息
      const enhancedFormData = {
        ...formData,
        message: `${initialMessage ? `${initialMessage}\n\n` : ''}${formData.message}`,
        source: source
      }

      // 获取当前语言
      const currentLanguage = t('common.languageCode', 'en') // 从翻译中获取语言代码
      
      const success = await sendEmail(enhancedFormData, currentLanguage)
      
      if (success) {
        // 短暂延迟后关闭模态框
        timeoutRef.current = setTimeout(() => {
          onClose()
        }, 3000)
      }
      
      return success
    } catch (error) {
      console.error('Contact form submission failed:', error)
      return false
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center"
          onClick={onClose}
        >
          {/* 背景遮罩 */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          
          {/* 模态框内容 */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 头部 */}
            <div className="relative bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-6 rounded-t-2xl">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
                disabled={isSubmitting}
              >
                <X size={24} />
              </button>
              
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-white/20 rounded-full">
                  <Mail size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">
                    {t('contact.title')}
                  </h2>
                  <p className="text-blue-100 mt-1">
                    {t('contact.subtitle')}
                  </p>
                </div>
              </div>
            </div>

            {/* 表单内容 */}
            <div className="px-8 py-6">
              <ContactForm
                onSubmit={handleFormSubmit}
                showTitle={false}
              />
            </div>

            {/* 底部信息 */}
            <div className="px-8 pb-6">
              <div className="text-center text-sm text-gray-500">
                <p>
                  {t('footer.company.description', 'Smart Library Solutions Provider')}
                </p>
                <p className="mt-2">
                  📞 400-996-7738 • ✉️ rfidlib@libmj.com
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default ContactModal